import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Checkbox, Chip, CircularProgress, Grid, IconButton, Select, Option, Stack, Typography, Input, Table, FormControl, FormLabel } from '@mui/joy';
import { FaCalendarWeek, FaChevronLeft, FaChevronRight, FaPoundSign } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import { directSupabase } from '../lib/supabaseClient';
import { getGraphClient } from '../utils/graphService';
import { WORK_CATEGORIES } from '../services/calendarService';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { computeTotals, DocumentLineInput, DocumentFeesConfig } from '../modules/docs/types/documents.types';
import {
  asPaid,
  averageWorkingDayOutgoings,
  exVat,
  outgoingCostForMonth,
  outgoingCostForMonths,
  outgoingCostForWeek,
  outgoingCostPerWorkingDay,
  type OutgoingRow,
  type VatTreatment,
} from '../utils/outgoingsBreakdown';

type ViewMode = 'day' | 'week' | 'fortnight' | 'month' | 'year';

interface ProjectEventRow {
  id: string;
  project_id: string;
  event_type: string;
  event_date: string; // ISO
  end_date?: string | null;
  metadata?: any | null; // JSONB metadata, can include trueCash: boolean
  calendar_event_id?: string | null;
}

const DEFAULT_MAGIC_NUMBER = 700; // GBP

const VAT_TREATMENT_LABELS: Record<string, string> = {
  inclusive: 'Inc VAT',
  exclusive: '+ VAT',
  none: 'No VAT',
};

function startOfDay(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function addDays(d: Date, n: number): Date { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt; }
function startOfWeekMonday(d: Date): Date {
  const day = d.getDay(); // 0 = Sun, 1 = Mon, ...
  const deltaToMonday = (day + 6) % 7; // Mon->0, Sun->6
  return addDays(startOfDay(d), -deltaToMonday);
}

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

function round2(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

const RevenueCalendarPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [anchorDate, setAnchorDate] = useState<Date>(startOfDay(new Date()));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicNumber, setMagicNumber] = useState<number>(DEFAULT_MAGIC_NUMBER);

  // Data
  const [events, setEvents] = useState<ProjectEventRow[]>([]);
  const [projectGross, setProjectGross] = useState<Record<string, number>>({}); // projectId -> gross (grand_total)
  const [projectNet, setProjectNet] = useState<Record<string, number>>({}); // projectId -> company profit (grand_total - all costs)
  const [projectMinusLabour, setProjectMinusLabour] = useState<Record<string, number>>({}); // projectId -> Profit (- all overheads)
  const [projectAfterVat, setProjectAfterVat] = useState<Record<string, number>>({}); // projectId -> net profit after VAT
  const [projectNetVatPayable, setProjectNetVatPayable] = useState<Record<string, number>>({}); // projectId -> net VAT payable (fixed from invoice)
  const [projectLabour, setProjectLabour] = useState<Record<string, number>>({}); // projectId -> labour (excl. VAT)
  const [projectInvoiceMap, setProjectInvoiceMap] = useState<Record<string, string>>({});
  const [invoiceRefById, setInvoiceRefById] = useState<Record<string, string>>({});
  const [eventCategoryById, setEventCategoryById] = useState<Record<string, string>>({}); // calendar_event_id -> category name
  const [eventSpanById, setEventSpanById] = useState<Record<string, { start: string; end: string }>>({}); // calendar_event_id -> start/end ISO
  const [expandedByPerson, setExpandedByPerson] = useState<Record<string, boolean>>({});
  const [projectNameById, setProjectNameById] = useState<Record<string, string>>({});
  const [projectCustomerNameById, setProjectCustomerNameById] = useState<Record<string, string>>({});
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualCategory, setManualCategory] = useState<string>('');
  const [manualAmount, setManualAmount] = useState<string>('');
  const [manualStart, setManualStart] = useState<string>('');
  const [manualEnd, setManualEnd] = useState<string>('');
  const [manualNotes, setManualNotes] = useState<string>('');
  const [manualAdditionalRanges, setManualAdditionalRanges] = useState<Array<{ startDate: string; endDate: string }>>([]);
  const [manualEntries, setManualEntries] = useState<Array<{ id: string; category_id: string; amount: number; start_date: string; end_date: string | null; notes: string | null }>>([]);
  const [showManageManual, setShowManageManual] = useState(false);
  const [editingEntry, setEditingEntry] = useState<null | { id: string; category_id: string; amount: number; start_date: string; end_date: string | null; notes: string | null; additionalRanges?: Array<{ startDate: string; endDate: string }> }>(null);
  const [manualRefreshKey, setManualRefreshKey] = useState(0);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  // Monthly outgoings state
  const [showOutgoings, setShowOutgoings] = useState(false);
  const [allOutgoings, setAllOutgoings] = useState<Array<{ id: string; name: string; category: string | null; amount: number; start_date: string; end_date: string | null; frequency: 'Once' | 'Daily' | 'Monthly' | 'Quarterly' | 'Annually'; notes: string | null; vat_treatment: VatTreatment | null }>>([]);
  const [editingOutgoing, setEditingOutgoing] = useState<null | { id?: string; name: string; category: string; amount: string; start_date: string; end_date: string; noEndDate: boolean; frequency: 'Once' | 'Daily' | 'Monthly' | 'Quarterly' | 'Annually'; notes: string; vat_treatment: VatTreatment | '' }>(null);
  const [outgoingsRefreshKey, setOutgoingsRefreshKey] = useState(0);
  const [outgoingsGranularity, setOutgoingsGranularity] = useState<'day' | 'week' | 'month' | 'quarter' | 'year'>('day');
  const OUTGOING_CATEGORIES = [
    'Insurance','Certification','Wages','Utility','Fuel','Rent','Advertising','Web','Equipment (Tools)','Equipment (Tech)','Motor','Maintenance','Other'
  ];
  const [editingMagicNumber, setEditingMagicNumber] = useState(false);
  const [magicNumberDraft, setMagicNumberDraft] = useState<number | null>(null);
  const [hasSessionMagicOverride, setHasSessionMagicOverride] = useState(false);

  const dateRange = useMemo(() => {
    if (viewMode === 'day') {
      const start = startOfDay(anchorDate);
      return { start, end: addDays(start, 1) };
    }
  if (viewMode === 'fortnight') {
      const start = startOfDay(anchorDate);
      return { start, end: addDays(start, 14) };
    }
    if (viewMode === 'month') {
      const first = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
      const nextMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 1);
      return { start: first, end: nextMonth };
    }
  if (viewMode === 'year') {
    const first = new Date(anchorDate.getFullYear(), 0, 1);
    const nextYear = new Date(anchorDate.getFullYear() + 1, 0, 1);
    return { start: first, end: nextYear };
  }
    // week (7 days) default, aligned to Monday
    const start = startOfWeekMonday(anchorDate);
    return { start, end: addDays(start, 7) };
  }, [viewMode, anchorDate]);

  const visibleDays = useMemo(() => {
    const days: Date[] = [];
    for (let d = new Date(dateRange.start); d < dateRange.end; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    return days;
  }, [dateRange]);

  const visibleMonths = useMemo(() => {
    if (viewMode !== 'year') return [] as Array<{ start: Date; end: Date }>; 
    const year = anchorDate.getFullYear();
    const months: Array<{ start: Date; end: Date }> = [];
    for (let m = 0; m < 12; m++) {
      const start = new Date(year, m, 1);
      const end = new Date(year, m + 1, 1);
      months.push({ start, end });
    }
    return months;
  }, [viewMode, anchorDate]);

  // Load every outgoing: the Magic Number averages the next 12 months, whatever year is on screen
  useEffect(() => {
    (async () => {
      try {
        const { data, error: outErr } = await directSupabase
          .from('monthly_outgoings')
          .select('*') // vat_treatment may not exist until its migration is applied
          .order('start_date', { ascending: true });
        if (outErr) {
          console.error('Error loading outgoings:', outErr);
        }
        setAllOutgoings(((data as any[]) || []).map(r => ({
          id: r.id,
          name: r.name,
          category: r.category ?? null,
          amount: Number(r.amount) || 0,
          start_date: r.start_date,
          end_date: r.end_date || null,
          frequency: (r.frequency as any) || 'Monthly',
          notes: r.notes ?? null,
          vat_treatment: (['inclusive', 'exclusive', 'none'].includes(r.vat_treatment) ? r.vat_treatment : null) as VatTreatment | null
        })));
      } catch {
        setAllOutgoings([]);
      }
    })();
  }, [outgoingsRefreshKey]);

  // Outgoings active in the year on screen (Manage table and breakdown card)
  const outgoings = useMemo(() => {
    const yearStart = `${anchorDate.getFullYear()}-01-01`;
    const nextYearStart = `${anchorDate.getFullYear() + 1}-01-01`;
    return allOutgoings.filter(o => {
      // No end date runs on; a one-off only applies on its start date
      const end = o.end_date || (o.frequency === 'Once' ? o.start_date : null);
      return o.start_date <= nextYearStart && (!end || end >= yearStart);
    });
  }, [allOutgoings, anchorDate]);

  // Helpers for outgoings display
  const monthIndex = anchorDate.getMonth();
  const yearIndex = anchorDate.getFullYear();
  const anchorQuarterStartMonth = Math.floor(monthIndex / 3) * 3; // 0,3,6,9

  const outgoingsDisplay = useMemo(() => {
    // Hide any legacy config rows (e.g. __MAGIC_NUMBER__) from the Outgoings card
    const rows = outgoings.filter(o => o.name !== '__MAGIC_NUMBER__');
    // Every view comes from the table's amount × frequency (see outgoingsBreakdown)
    const breakdown = (cost: (row: OutgoingRow) => number, label: string) => {
      const list = rows
        .map(r => {
          const fromAmount = cost(r);
          return { ...r, adjusted: asPaid(r, fromAmount), adjustedExVat: exVat(r, fromAmount) };
        })
        .filter(r => r.adjusted !== 0);
      const total = list.reduce((s, r) => s + (r.adjusted || 0), 0);
      const totalExVat = list.reduce((s, r) => s + (r.adjustedExVat || 0), 0);
      return { list, total, totalExVat, label };
    };
    if (outgoingsGranularity === 'day') {
      // This month's cost of each row spread over the month's weekdays
      return breakdown(r => outgoingCostPerWorkingDay(r, yearIndex, monthIndex), 'Daily');
    }
    if (outgoingsGranularity === 'week') {
      const weekStart = startOfWeekMonday(anchorDate);
      return breakdown(r => outgoingCostForWeek(r, weekStart), 'Weekly');
    }
    if (outgoingsGranularity === 'month') {
      return breakdown(r => outgoingCostForMonth(r, yearIndex, monthIndex), 'Monthly');
    }
    if (outgoingsGranularity === 'quarter') {
      return breakdown(r => outgoingCostForMonths(r, yearIndex, anchorQuarterStartMonth, 3), 'Quarterly');
    }
    return breakdown(r => outgoingCostForMonths(r, yearIndex, 0, 12), 'Annual');
  }, [outgoings, outgoingsGranularity, anchorDate, monthIndex, yearIndex, anchorQuarterStartMonth]);

  // Magic Number defaults to the average outgoings per working day over the next 12 months,
  // so it holds steady whichever month or breakdown is on screen.
  // Once the user saves an override in this session, we stop auto-syncing until refresh.
  const averageDailyOutgoings = useMemo(
    () => averageWorkingDayOutgoings(allOutgoings.filter(o => o.name !== '__MAGIC_NUMBER__'), new Date()),
    [allOutgoings],
  );
  useEffect(() => {
    if (hasSessionMagicOverride || editingMagicNumber) return;
    const next = round2(averageDailyOutgoings);
    if (Number.isFinite(next) && next > 0 && next !== magicNumber) {
      setMagicNumber(next);
    }
  }, [hasSessionMagicOverride, editingMagicNumber, averageDailyOutgoings, magicNumber]);

  // Persist magic number so other dashboards can reuse the same baseline
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('revenueCalendar.magicNumber', String(magicNumber));
      }
    } catch {
      // Ignore storage errors (Safari private mode, etc.)
    }
  }, [magicNumber]);

  // Weekly summary (moved below dayAggregates)

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        // 1) Fetch scheduled project events in an expanded range to capture cross-boundary spans
        const expandedStart = addDays(dateRange.start, -14);
        const expandedEnd = addDays(dateRange.end, 14);
        const { data: peData, error: peError } = await directSupabase
          .from('project_events')
          .select('id, project_id, event_type, event_date, end_date, calendar_event_id, metadata')
          .eq('event_type', 'installationDateConfirmation')
          .gte('event_date', expandedStart.toISOString())
          .lt('event_date', expandedEnd.toISOString())
          .order('event_date', { ascending: true });

        if (peError) throw new Error(peError.message);
        const rows: ProjectEventRow[] = (peData || []) as any;
        let rowsToUse: ProjectEventRow[] = rows;

        // 2) Resolve Outlook categories for these events (by calendar_event_id)
        const calendarIds = Array.from(new Set(rows.map(r => r.calendar_event_id).filter(Boolean))) as string[];
        if (calendarIds.length > 0) {
          try {
            const client = await getGraphClient();
            // Find main calendar id by name
            const calendarsResponse = await client.api('/me/calendars').get();
            const cal = (calendarsResponse?.value || []).find((c: any) => c.name === 'Calendar');
            const calendarId = cal?.id;
            if (calendarId) {
              const BATCH_LIMIT = 20;
              const catMap: Record<string, string> = {};
              const spanMap: Record<string, { start: string; end: string }> = {};
              const existingOutlookIds = new Set<string>();
              for (let i = 0; i < calendarIds.length; i += BATCH_LIMIT) {
                const batchIds = calendarIds.slice(i, i + BATCH_LIMIT);
                const batchRequests = batchIds.map((id, idx) => ({
                  id: String(idx + 1),
                  method: 'GET',
                  url: `/me/calendars/${calendarId}/events/${id}?$select=id,categories,start,end`
                }));
                const batchResp = await client.api('/$batch').post({ requests: batchRequests });
                const responses = batchResp?.responses || [];
                responses.forEach((r: any) => {
                  if (r.status === 200 && r.body) {
                    const ev = r.body;
                    const cat = Array.isArray(ev.categories) && ev.categories.length > 0 ? ev.categories[0] : 'unassigned';
                    catMap[ev.id] = cat;
                    if (ev.start?.dateTime && ev.end?.dateTime) {
                      spanMap[ev.id] = { start: ev.start.dateTime, end: ev.end.dateTime };
                    }
                    existingOutlookIds.add(ev.id);
                  }
                });
              }
              setEventCategoryById(catMap);
              setEventSpanById(spanMap);

              // Hide project events that point to a deleted Outlook event (present calendar_event_id, but not found in Outlook)
              rowsToUse = rows.filter(r => !r.calendar_event_id || existingOutlookIds.has(r.calendar_event_id as string));

              // Deduplicate per project/day: prefer events that have a valid Outlook span over unassigned
              const bestByProjectDay: Record<string, ProjectEventRow> = {};
              const scoreOf = (ev: ProjectEventRow): number => {
                const id = (ev.calendar_event_id || '') as string;
                if (id && spanMap[id]) return 2; // has Outlook + span
                if (id) return 1; // has Outlook id only
                return 0; // no Outlook link
              };
              rowsToUse.forEach(ev => {
                const dayIso = startOfDay(new Date(ev.event_date)).toISOString();
                const key = `${ev.project_id}|${dayIso}`;
                const prev = bestByProjectDay[key];
                if (!prev) {
                  bestByProjectDay[key] = ev;
                } else {
                  if (scoreOf(ev) > scoreOf(prev)) bestByProjectDay[key] = ev;
                }
              });
              rowsToUse = Object.values(bestByProjectDay);
            }
          } catch (e) {
            // Non-fatal if categories cannot be fetched
            console.warn('Could not fetch Outlook categories for revenue calendar:', e);
          }
        }

        // Use filtered rows if available
        setEvents(rowsToUse);

        // 3) Find linked invoice references for these projects
        const projectIds = Array.from(new Set(rowsToUse.map(r => r.project_id))).filter(Boolean);
        let projectGrossMap: Record<string, number> = {};
        let projectNetMap: Record<string, number> = {};
        let projectMinusLabourMap: Record<string, number> = {};
        let projectAfterVatMap: Record<string, number> = {};
        let projectNetVatPayableMap: Record<string, number> = {};
        let projectLabourMap: Record<string, number> = {};

        if (projectIds.length > 0) {
          const { data: docRows, error: docErr } = await directSupabase
            .from('documents')
            .select(`id, project_id, lifecycle_ref, invoice_ref, title, net_total, vat_amount, gross_total, updated_at, created_at, admin_pct, admin_cap, contingency_pct, profit_pct, developer_pct, vat_enabled, vat_pct, document_lines(line_kind, qty, unit_price, line_total, vat_inclusive, labour_rate_id, name, unit, labour_days, labour_hours)`)
            .in('project_id', projectIds as string[])
            .eq('kind', 'Invoice');

          if (docErr) {
            console.error('Error loading invoice totals for revenue calendar:', docErr);
          }

          const round2 = (v: number) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;
          const grossMap: Record<string, number> = {};
          const netMap: Record<string, number> = {};
          const minusLabourMap: Record<string, number> = {};
          const afterVatMap: Record<string, number> = {};
          const labourMap: Record<string, number> = {};
          const invoiceLinkMap: Record<string, string> = {};
          const invoiceRefMap: Record<string, string> = {};
          (docRows || []).forEach((row: any) => {
            const pid = row.project_id;
            const netTotal = Number(row.net_total || row.gross_total || 0);
            const vatAmount = Number(row.vat_amount || 0);
            const grossTotal = Number(row.gross_total || netTotal + vatAmount);
            // Without legacy breakdowns, treat stored net_total as our “company profit” and minus-labour proxy
            const profit = netTotal;

            const mappedLines: DocumentLineInput[] = Array.isArray(row.document_lines)
              ? row.document_lines.map((line: any) => ({
                  lineKind: (line.line_kind || 'other') as DocumentLineInput['lineKind'],
                  qty: Number(line.qty) || 0,
                  unitPrice: Number(line.unit_price) || 0,
                  lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
                  vatInclusive: line.vat_inclusive ?? false,
                }))
              : [];

            const fees: DocumentFeesConfig = {
              adminPct: Number(row.admin_pct ?? 0),
              adminCap: Number(row.admin_cap ?? 0),
              contingencyPct: Number(row.contingency_pct ?? 0),
              profitPct: Number(row.profit_pct ?? 0),
              developerPct: row.developer_pct != null ? Number(row.developer_pct) : null,
            };

            const vatEnabled = row.vat_enabled !== false;
            const vatPct = typeof row.vat_pct === 'number' ? Number(row.vat_pct) : 0.2;

            let minusLabour = netTotal;
            let profitAfterVat = minusLabour;

            if (mappedLines.length > 0) {
              try {
                const totals = computeTotals(mappedLines, fees, { enabled: vatEnabled, pct: vatPct });
                const baseProfitOnly = round2(totals.baseCost * (fees.profitPct ?? 0));
                minusLabour = round2(totals.adminFee + totals.contingency + baseProfitOnly);
                const labourExVat = round2(totals.labourTotal || 0);
                labourMap[pid] = (labourMap[pid] || 0) + labourExVat;

                if (vatEnabled) {
                  const subtotal = round2(totals.preProfit + totals.profit);
                  const totalVat = row.vat_amount != null ? Number(row.vat_amount) : round2(subtotal * vatPct);
                  const goodsVat = round2(
                    mappedLines
                      .filter((l) => l.lineKind === 'material' || l.lineKind === 'equipment' || l.lineKind === 'other')
                      .reduce((acc, l) => {
                        const gross = round2(
                          l.lineTotal != null ? Number(l.lineTotal) : (Number(l.qty) || 0) * (Number(l.unitPrice) || 0)
                        );
                        const vatPortion = l.vatInclusive ? round2(gross * (vatPct / (1 + vatPct))) : round2(gross * vatPct);
                        return acc + vatPortion;
                      }, 0)
                  );
                  const netVat = round2(totalVat - goodsVat);
                  const netVatPayable = netVat > 0 ? netVat : 0;
                  profitAfterVat = round2(minusLabour - netVatPayable);
                  // Store netVatPayable per project (fixed from invoice)
                  if (!projectNetVatPayableMap[pid]) {
                    projectNetVatPayableMap[pid] = netVatPayable;
                  }
                } else {
                  profitAfterVat = minusLabour;
                  if (!projectNetVatPayableMap[pid]) {
                    projectNetVatPayableMap[pid] = 0;
                  }
                }
              } catch (calcErr) {
                console.warn('Failed to compute profit metrics for invoice', row.id, calcErr);
              }
            }

            grossMap[pid] = (grossMap[pid] || 0) + grossTotal;
            netMap[pid] = (netMap[pid] || 0) + profit;
            minusLabourMap[pid] = (minusLabourMap[pid] || 0) + minusLabour;
            afterVatMap[pid] = (afterVatMap[pid] || 0) + profitAfterVat;

            const currentInvoiceId = invoiceLinkMap[pid];
            if (!currentInvoiceId) {
              invoiceLinkMap[pid] = row.id;
            } else {
              const existing = (docRows || []).find((d: any) => d.id === currentInvoiceId);
              if (!existing || new Date(row.updated_at || row.created_at || 0) > new Date(existing.updated_at || existing.created_at || 0)) {
                invoiceLinkMap[pid] = row.id;
              }
            }
          });

          projectGrossMap = grossMap;
          projectNetMap = netMap;
          projectMinusLabourMap = minusLabourMap;
          projectAfterVatMap = afterVatMap;
          projectNetVatPayableMap = projectNetVatPayableMap;
          projectLabourMap = labourMap;

          // For retrospectively linked events: if a project has events but no invoice data,
          // try to fetch invoice data for that project
          const projectsWithEventsButNoInvoice = projectIds.filter(pid => !invoiceLinkMap[pid]);
          if (projectsWithEventsButNoInvoice.length > 0) {
            // Fetch invoices for these projects
            const { data: missingDocRows } = await directSupabase
              .from('documents')
              .select(`id, project_id, lifecycle_ref, invoice_ref, title, net_total, vat_amount, gross_total, updated_at, created_at, admin_pct, admin_cap, contingency_pct, profit_pct, developer_pct, vat_enabled, vat_pct, document_lines(line_kind, qty, unit_price, line_total, vat_inclusive, labour_rate_id, name, unit, labour_days, labour_hours)`)
              .in('project_id', projectsWithEventsButNoInvoice as string[])
              .eq('kind', 'Invoice');
            
            if (missingDocRows && missingDocRows.length > 0) {
              // Process missing invoices the same way as above
              missingDocRows.forEach((row: any) => {
                const pid = row.project_id;
                if (invoiceLinkMap[pid]) return; // Already processed
                
                const netTotal = Number(row.net_total || row.gross_total || 0);
                const vatAmount = Number(row.vat_amount || 0);
                const grossTotal = Number(row.gross_total || netTotal + vatAmount);
                const profit = netTotal;
                
                const mappedLines: DocumentLineInput[] = Array.isArray(row.document_lines)
                  ? row.document_lines.map((line: any) => ({
                      lineKind: (line.line_kind || 'other') as DocumentLineInput['lineKind'],
                      qty: Number(line.qty) || 0,
                      unitPrice: Number(line.unit_price) || 0,
                      lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
                      vatInclusive: line.vat_inclusive ?? false,
                    }))
                  : [];
                
                const fees: DocumentFeesConfig = {
                  adminPct: Number(row.admin_pct ?? 0),
                  adminCap: Number(row.admin_cap ?? 0),
                  contingencyPct: Number(row.contingency_pct ?? 0),
                  profitPct: Number(row.profit_pct ?? 0),
                  developerPct: row.developer_pct != null ? Number(row.developer_pct) : null,
                };
                
                const vatEnabled = row.vat_enabled !== false;
                const vatPct = typeof row.vat_pct === 'number' ? Number(row.vat_pct) : 0.2;
                
                let minusLabour = netTotal;
                let profitAfterVat = minusLabour;
                
                if (mappedLines.length > 0) {
                  try {
                    const totals = computeTotals(mappedLines, fees, { enabled: vatEnabled, pct: vatPct });
                    const baseProfitOnly = round2(totals.baseCost * (fees.profitPct ?? 0));
                    minusLabour = round2(totals.adminFee + totals.contingency + baseProfitOnly);
                    const labourExVat = round2(totals.labourTotal || 0);
                    projectLabourMap[pid] = (projectLabourMap[pid] || 0) + labourExVat;
                    
                    if (vatEnabled) {
                      const subtotal = round2(totals.preProfit + totals.profit);
                      const totalVat = row.vat_amount != null ? Number(row.vat_amount) : round2(subtotal * vatPct);
                      const goodsVat = round2(
                        mappedLines
                          .filter((l) => l.lineKind === 'material' || l.lineKind === 'equipment' || l.lineKind === 'other')
                          .reduce((acc, l) => {
                            const gross = round2(
                              l.lineTotal != null ? Number(l.lineTotal) : (Number(l.qty) || 0) * (Number(l.unitPrice) || 0)
                            );
                            const vatPortion = l.vatInclusive ? round2(gross * (vatPct / (1 + vatPct))) : round2(gross * vatPct);
                            return acc + vatPortion;
                          }, 0)
                      );
                      const netVat = round2(totalVat - goodsVat);
                      const netVatPayable = netVat > 0 ? netVat : 0;
                      profitAfterVat = round2(minusLabour - netVatPayable);
                      // Store netVatPayable per project (fixed from invoice)
                      if (!projectNetVatPayableMap[pid]) {
                        projectNetVatPayableMap[pid] = netVatPayable;
                      }
                    } else {
                      profitAfterVat = minusLabour;
                      if (!projectNetVatPayableMap[pid]) {
                        projectNetVatPayableMap[pid] = 0;
                      }
                    }
                  } catch (calcErr) {
                    console.warn('Failed to compute profit metrics for missing invoice', row.id, calcErr);
                  }
                }
                
                projectGrossMap[pid] = (projectGrossMap[pid] || 0) + grossTotal;
                projectNetMap[pid] = (projectNetMap[pid] || 0) + profit;
                projectMinusLabourMap[pid] = (projectMinusLabourMap[pid] || 0) + minusLabour;
                projectAfterVatMap[pid] = (projectAfterVatMap[pid] || 0) + profitAfterVat;
                
                // Add to invoice map
                const currentInvoiceId = invoiceLinkMap[pid];
                if (!currentInvoiceId) {
                  invoiceLinkMap[pid] = row.id;
                } else {
                  const existing = (docRows || []).find((d: any) => d.id === currentInvoiceId);
                  if (!existing || new Date(row.updated_at || row.created_at || 0) > new Date(existing.updated_at || existing.created_at || 0)) {
                    invoiceLinkMap[pid] = row.id;
                  }
                }

                const refMissing = (row.invoice_ref as string | null) || (row.lifecycle_ref as string | null) || (row.id ? String(row.id).slice(0, 8) : '');
                if (row.id && refMissing) {
                  invoiceRefMap[row.id] = refMissing;
                }
                
                // Extract labour lines (used only for labour totals and VAT calculations)
                const labourLines = Array.isArray(row.document_lines)
                  ? row.document_lines
                      .filter((line: any) => line.line_kind === 'labour')
                  .map((line: any) => ({
                        id: line.id || '',
                        name: line.name || 'Labour',
                        qty: Number(line.qty) || 0,
                        unit: line.unit || 'day',
                        unitPrice: Number(line.unit_price) || 0,
                        total: line.line_total != null ? Number(line.line_total) : (Number(line.qty) || 0) * (Number(line.unit_price) || 0),
                        labourRateId: line.labour_rate_id || null
                      }))
                  : [];
              });
              
              // Update state with missing invoice data
              setProjectInvoiceMap(prev => ({ ...prev, ...invoiceLinkMap }));
              setProjectGross(prev => ({ ...prev, ...projectGrossMap }));
              setProjectNet(prev => ({ ...prev, ...projectNetMap }));
              setProjectMinusLabour(prev => ({ ...prev, ...projectMinusLabourMap }));
              setProjectAfterVat(prev => ({ ...prev, ...projectAfterVatMap }));
              setProjectNetVatPayable(prev => ({ ...prev, ...projectNetVatPayableMap }));
              setProjectLabour(prev => ({ ...prev, ...projectLabourMap }));
            }
          }

        setInvoiceRefById(prev => ({ ...prev, ...invoiceRefMap }));
        setProjectGross(projectGrossMap);
        setProjectNet(projectNetMap);

        // 4) Fetch project names and customer names for display
        if (projectIds.length > 0) {
          try {
            const { data: projRows } = await directSupabase
              .from('projects')
              .select('id, project_name, customer_name')
              .in('id', projectIds as string[]);
            const nameMap: Record<string, string> = {};
            const customerMap: Record<string, string> = {};
            (projRows || []).forEach((p: any) => {
              nameMap[p.id] = p.project_name || 'Project';
              if (p.customer_name) {
                customerMap[p.id] = p.customer_name;
              }
            });
            setProjectNameById(nameMap);
            setProjectCustomerNameById(customerMap);
          } catch {}
        } else {
          setProjectNameById({});
          setProjectCustomerNameById({});
        }
          setProjectMinusLabour(projectMinusLabourMap || {});
          setProjectAfterVat(projectAfterVatMap || {});
          setProjectNetVatPayable(projectNetVatPayableMap || {});
          setProjectLabour(projectLabourMap || {});
          setProjectInvoiceMap(invoiceLinkMap);

        }
      } catch (e: any) {
        console.error('Error loading revenue schedule:', e);
        setError(e?.message || 'Failed to load revenue schedule');
      } finally {
        setLoading(false);
      }
    })();
  }, [dateRange.start, dateRange.end]);

  // Fetch manual entries in range and fold into aggregates
  useEffect(() => {
    (async () => {
      try {
        const { data } = await directSupabase
          .from('manual_revenue_entries')
          .select('id, category_id, amount, start_date, end_date, notes')
          .lte('start_date', dateRange.end.toISOString().slice(0,10))
          .gte('end_date', dateRange.start.toISOString().slice(0,10));
        const entries: any[] = (data as any[]) || [];
        // Also store entries for management UI
        setManualEntries(entries.map(e => ({
          id: e.id,
          category_id: e.category_id,
          amount: Number(e.amount) || 0,
          start_date: e.start_date,
          end_date: e.end_date || null,
          notes: (e as any).notes ?? null
        })));
        // Merge into project-level maps using a synthetic key per entry (we don't store per-project here, aggregate straight into dayAggregates downstream via side map)
        // Build a map day -> category -> amounts
        const addMap: Record<string, { byCatGross: Record<string, number>; byCatNet: Record<string, number>; byCatAfterLabour: Record<string, number>; byCatAfterVat: Record<string, number>; gross: number; net: number; magic: number; afterVat: number }> = {};
        entries.forEach((m) => {
          // Check for additional work ranges stored in notes (as JSON)
          let workDays: Date[] = [];
          let notesData: any = null;
          try {
            if (m.notes) {
              notesData = JSON.parse(m.notes);
            }
          } catch {
            // If notes isn't JSON, treat as plain text
            notesData = { userNotes: m.notes };
          }
          
          if (notesData && Array.isArray(notesData.additionalWorkRanges)) {
            // Use explicit work date ranges from notes
            const ranges = notesData.additionalWorkRanges;
            const mainRange = { start: m.start_date, end: m.end_date || m.start_date };
            const allRanges = [mainRange, ...ranges.map((r: any) => ({ start: r.startDate, end: r.endDate || r.startDate }))];
            // Collect all business days from all ranges
            allRanges.forEach((range: { start: string; end: string }) => {
              const rangeStart = toDateOnly(range.start);
              const rangeEnd = toDateOnly(range.end);
              const rangeDays = iterateBusinessDaysInclusive(rangeStart, rangeEnd);
              workDays.push(...rangeDays);
            });
            // Remove duplicates and sort
            const uniqueDays = Array.from(new Set(workDays.map(d => d.toISOString()))).map(iso => new Date(iso)).sort((a, b) => a.getTime() - b.getTime());
            workDays = uniqueDays;
          } else {
            // Fallback to original logic: use consecutive days
          const s = toDateOnly(m.start_date);
          const e = toDateOnly(m.end_date || m.start_date);
            workDays = iterateBusinessDaysInclusive(s, e);
          }
          const days = workDays;
          const perDay = (Number(m.amount) || 0) / Math.max(1, days.length);
          days.forEach((d) => {
            const k = startOfDay(d).toISOString();
            if (!addMap[k]) addMap[k] = { byCatGross: {}, byCatNet: {}, byCatAfterLabour: {}, byCatAfterVat: {}, gross: 0, net: 0, magic: 0, afterVat: 0 };
            const catName = WORK_CATEGORIES.find(c => c.id === m.category_id)?.name || m.category_id;
            addMap[k].byCatGross[catName] = (addMap[k].byCatGross[catName] || 0) + perDay;
            // Manual entry: treat amount as gross and profit (no cost breakdown provided), and as Magic metric too
            addMap[k].byCatNet[catName] = (addMap[k].byCatNet[catName] || 0) + perDay;
            addMap[k].byCatAfterLabour[catName] = (addMap[k].byCatAfterLabour[catName] || 0) + perDay;
            addMap[k].byCatAfterVat[catName] = (addMap[k].byCatAfterVat[catName] || 0) + perDay;
            addMap[k].gross += perDay;
            addMap[k].net += perDay;
            addMap[k].magic += perDay;
            addMap[k].afterVat += perDay;
          });
        });
        // Store addMap in a ref-like closure by mutating dayAggregates when we compute
        manualAddsRef.current = addMap;
      } catch {
        manualAddsRef.current = {} as any;
      }
    })();
  }, [dateRange.start, dateRange.end, manualRefreshKey]);

  const manualAddsRef = React.useRef<Record<string, { byCatGross: Record<string, number>; byCatNet: Record<string, number>; byCatAfterLabour: Record<string, number>; byCatAfterVat: Record<string, number>; gross: number; net: number; magic: number; afterVat: number }>>({});

  const dateRangeLabel = (start: Date, end: Date) => {
    const s = start.toISOString().slice(0,10);
    const e = end.toISOString().slice(0,10);
    return `${s} to ${e}`;
  };

  // Aggregate per day and per category
  const toDateOnly = (iso: string): Date => {
    const d = new Date(iso);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const iterateDaysInclusive = (start: Date, end: Date): Date[] => {
    const days: Date[] = [];
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    for (let d = new Date(s); d <= e; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    return days;
  };

  const isWeekday = (d: Date): boolean => {
    const dow = d.getDay();
    return dow >= 1 && dow <= 5; // Mon-Fri
  };

  const iterateBusinessDaysInclusive = (start: Date, end: Date): Date[] => {
    return iterateDaysInclusive(start, end).filter(isWeekday);
  };

  const dayAggregates = useMemo(() => {
    const byDay: Record<string, { totalGross: number; totalNet: number; totalMinusLabour: number; totalAfterVat: number; totalNetVatPayable: number; totalLabour: number; byCategoryGross: Record<string, number>; byCategoryNet: Record<string, number>; byCategoryAfterLabour: Record<string, number>; byCategoryAfterVat: Record<string, number>; distinctCategories: Set<string> } > = {};
    const categoryNameById = new Map(WORK_CATEGORIES.map(c => [c.id, c.name] as const));

    events.forEach(ev => {
      const assigned = (ev.calendar_event_id && eventCategoryById[ev.calendar_event_id]) || 'unassigned';
      // Prefer a known work category display name if it matches, else show Outlook category name
      const categoryName = categoryNameById.get(assigned) || assigned;
      // Check if this event is marked as "True Cash" - if so, use manual cash amount instead of invoice values
      const isTrueCash = ev.metadata && typeof ev.metadata === 'object' && (ev.metadata as any).trueCash === true;
      const trueCashAmount = isTrueCash && ev.metadata && typeof ev.metadata === 'object' ? Number((ev.metadata as any).trueCashAmount) || 0 : 0;
      
      // Determine actual work days - check for explicit work ranges in metadata
      let workDays: Date[] = [];
      if (ev.metadata && typeof ev.metadata === 'object' && Array.isArray((ev.metadata as any).additionalWorkRanges)) {
        // Use explicit work date ranges from metadata
        const ranges = (ev.metadata as any).additionalWorkRanges;
        const mainRange = { start: ev.event_date, end: ev.end_date || ev.event_date };
        const allRanges = [mainRange, ...ranges.map((r: any) => ({ start: r.startDate, end: r.endDate || r.startDate }))];
        // Collect all business days from all ranges
        allRanges.forEach((range: { start: string; end: string }) => {
          const rangeStart = toDateOnly(range.start);
          const rangeEnd = toDateOnly(range.end);
          const rangeDays = iterateBusinessDaysInclusive(rangeStart, rangeEnd);
          workDays.push(...rangeDays);
        });
        // Remove duplicates and sort
        const uniqueDays = Array.from(new Set(workDays.map(d => d.toISOString()))).map(iso => new Date(iso)).sort((a, b) => a.getTime() - b.getTime());
        workDays = uniqueDays;
      } else {
        // Fallback to original logic: use event span
      let startDateOnly = startOfDay(new Date(ev.event_date));
      let endDateOnly = startDateOnly;
      if (ev.calendar_event_id && eventSpanById[ev.calendar_event_id]) {
        const span = eventSpanById[ev.calendar_event_id];
        startDateOnly = toDateOnly(span.start);
        endDateOnly = toDateOnly(span.end);
      } else if (ev.end_date) {
        // Fallback to DB end_date when Outlook link is missing
        endDateOnly = toDateOnly(ev.end_date);
      }
        workDays = iterateBusinessDaysInclusive(startDateOnly, endDateOnly);
      }
      const days = workDays;
      
      // For True Cash events, use the manual amount (excl. VAT) and derive other metrics
      // For regular events, use invoice-derived values
      let gross: number;
      let net: number;
      let minusLabour: number;
      let afterVat: number;
      let labourExVat: number;
      
      if (isTrueCash && trueCashAmount > 0) {
        // True Cash: amount is already excl. VAT, so use it as both gross and net
        // We don't have breakdowns for cash, so we'll use simplified calculations
        gross = trueCashAmount; // Cash amount is the total received
        net = trueCashAmount; // Same as gross for cash (no VAT)
        // For cash, we can't calculate minusLabour/afterVat without invoice breakdown
        // So we'll set them to the same as net for now (or could be 0, but net makes more sense)
        minusLabour = trueCashAmount;
        afterVat = trueCashAmount;
        labourExVat = 0; // No labour data for cash transactions
      } else {
        // Regular invoice-based event
        gross = projectGross[ev.project_id] || 0;
        net = projectNet[ev.project_id] || 0;
        // Use invoice labour and invoice-derived profit metrics
        labourExVat = projectLabour[ev.project_id] || 0;
        minusLabour = projectMinusLabour[ev.project_id] || 0;
        // VAT and after-VAT profit are fixed from the invoice
        afterVat = projectAfterVat[ev.project_id] || 0;
      }
      const shareGross = days.length > 0 ? gross / days.length : gross;
      const shareNet = days.length > 0 ? net / days.length : net;
      const shareMinusLabour = days.length > 0 ? minusLabour / days.length : minusLabour;
      const shareAfterVat = days.length > 0 ? afterVat / days.length : afterVat;
      const shareNetVatPayable = days.length > 0 ? (projectNetVatPayable[ev.project_id] || 0) / days.length : (projectNetVatPayable[ev.project_id] || 0);
      const shareLabour = days.length > 0 ? labourExVat / days.length : labourExVat;
      days.forEach(day => {
        const dayKey = startOfDay(day).toISOString();
        if (!byDay[dayKey]) byDay[dayKey] = { totalGross: 0, totalNet: 0, totalMinusLabour: 0, totalAfterVat: 0, totalNetVatPayable: 0, totalLabour: 0, byCategoryGross: {}, byCategoryNet: {}, byCategoryAfterLabour: {}, byCategoryAfterVat: {}, distinctCategories: new Set() };
        const entry = byDay[dayKey];
        entry.byCategoryGross[categoryName] = (entry.byCategoryGross[categoryName] || 0) + shareGross;
        entry.byCategoryNet[categoryName] = (entry.byCategoryNet[categoryName] || 0) + shareNet;
        entry.byCategoryAfterLabour[categoryName] = (entry.byCategoryAfterLabour[categoryName] || 0) + shareMinusLabour;
        entry.byCategoryAfterVat[categoryName] = (entry.byCategoryAfterVat[categoryName] || 0) + shareAfterVat;
        entry.totalGross += shareGross;
        entry.totalNet += shareNet;
        entry.totalMinusLabour += shareMinusLabour;
        entry.totalAfterVat += shareAfterVat;
        entry.totalNetVatPayable += shareNetVatPayable;
        entry.totalLabour += shareLabour;
        entry.distinctCategories.add(categoryName);
      });
    });

    // Merge manual additions
    const adds = manualAddsRef.current || {};
    Object.entries(adds).forEach(([dayKey, extra]) => {
      if (!byDay[dayKey]) byDay[dayKey] = { totalGross: 0, totalNet: 0, totalMinusLabour: 0, totalAfterVat: 0, totalNetVatPayable: 0, totalLabour: 0, byCategoryGross: {}, byCategoryNet: {}, byCategoryAfterLabour: {}, byCategoryAfterVat: {}, distinctCategories: new Set() };
      const entry = byDay[dayKey];
      entry.totalGross += extra.gross;
      entry.totalNet += extra.net;
      entry.totalMinusLabour += extra.magic;
      entry.totalAfterVat += extra.afterVat || extra.magic || 0;
      // Manual entries don't have VAT, so netVatPayable stays 0
      Object.entries(extra.byCatGross).forEach(([cat, val]) => {
        entry.byCategoryGross[cat] = (entry.byCategoryGross[cat] || 0) + val;
        entry.distinctCategories.add(cat);
      });
      Object.entries(extra.byCatNet).forEach(([cat, val]) => {
        entry.byCategoryNet[cat] = (entry.byCategoryNet[cat] || 0) + val;
      });
      Object.entries(extra.byCatAfterLabour || {}).forEach(([cat, val]) => {
        entry.byCategoryAfterLabour[cat] = (entry.byCategoryAfterLabour[cat] || 0) + val;
      });
      Object.entries(extra.byCatAfterVat || {}).forEach(([cat, val]) => {
        entry.byCategoryAfterVat[cat] = (entry.byCategoryAfterVat[cat] || 0) + val;
      });
    });

    return byDay;
  }, [events, projectGross, projectNet, projectMinusLabour, projectAfterVat, projectLabour, projectNetVatPayable, eventCategoryById, eventSpanById]);

  const yearSummary = useMemo(() => {
    if (viewMode !== 'year') return null;
    let totalGross = 0;
    let totalProfit = 0;
    let totalMagic = 0;
    let weekendBonusGross = 0;
    let weekendBonusProfit = 0;
    let weekdaysCount = 0;
    for (let d = new Date(dateRange.start); d < dateRange.end; d = addDays(d, 1)) {
      const key = startOfDay(d).toISOString();
      const agg = (dayAggregates as any)[key];
      const dow = d.getDay();
      const isWeekdayDay = dow >= 1 && dow <= 5;
      if (isWeekdayDay) {
        weekdaysCount += 1;
        if (agg) {
          totalGross += agg.totalGross;
          totalProfit += agg.totalNet;
          totalMagic += agg.totalMinusLabour;
        }
      } else if (agg) {
        weekendBonusGross += agg.totalGross;
        weekendBonusProfit += agg.totalNet;
      }
    }
    const target = weekdaysCount * magicNumber;
    const magicDelta = totalMagic - target;
    return { totalGross, totalProfit, totalMagic, weekendBonusGross, weekendBonusProfit, weekdaysCount, target, magicDelta };
  }, [viewMode, dateRange.start, dateRange.end, magicNumber, dayAggregates]);

  // Summary card (Mon–Fri when in week view; otherwise entire range)
  const weeklySummary = useMemo(() => {
    if (visibleDays.length === 0) return null;
    let totalGross = 0;
    let totalProfit = 0; // company profit
    let totalMagic = 0; // minus labour metric
    let weekendBonusGross = 0;
    let weekendBonusProfit = 0;
    let weekdaysCount = 0;
    visibleDays.forEach((day) => {
      const key = startOfDay(day).toISOString();
      const agg = (dayAggregates as any)[key];
      const dow = day.getDay();
      const isWeekdayDay = dow >= 1 && dow <= 5; // Mon–Fri in any view
      if (isWeekdayDay) {
        weekdaysCount += 1;
        if (agg) {
          totalGross += agg.totalGross;
          totalProfit += agg.totalNet;
          totalMagic += agg.totalMinusLabour;
        }
      } else if (agg) {
        // Weekend bonus only when we actually have revenue
        weekendBonusGross += agg.totalGross;
        weekendBonusProfit += agg.totalNet;
      }
    });
    const weeklyTarget = weekdaysCount * magicNumber;
    const grossDelta = totalGross - weeklyTarget;
    const magicDelta = totalMagic - weeklyTarget;
    return {
      totalGross,
      totalProfit,
      totalMagic,
      weekendBonusGross,
      weekendBonusProfit,
      weekdaysCount,
      weeklyTarget,
      grossDelta,
      magicDelta
    };
  }, [visibleDays, magicNumber, dayAggregates]);

  const changeAnchor = (deltaDays: number) => setAnchorDate(prev => addDays(prev, deltaDays));

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconWrapper icon={FaCalendarWeek} style={{ fontSize: '1.5rem' }} />
          <Typography level="h3">Revenue Schedule</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Select value={viewMode} onChange={(_, v) => setViewMode((v || 'week') as ViewMode)} size="sm">
            <Option value="day">Day</Option>
            <Option value="week">Week</Option>
            <Option value="fortnight">Fortnight</Option>
            <Option value="month">Month</Option>
            <Option value="year">Year</Option>
          </Select>
          <IconButton size="sm" onClick={() => changeAnchor(viewMode === 'month' ? -30 : (viewMode === 'year' ? -365 : -7))}>
            <IconWrapper icon={FaChevronLeft} />
          </IconButton>
          <Button size="sm" variant="outlined" onClick={() => setAnchorDate(startOfDay(new Date()))}>Today</Button>
          <IconButton size="sm" onClick={() => changeAnchor(viewMode === 'month' ? 30 : (viewMode === 'year' ? 365 : 7))}>
            <IconWrapper icon={FaChevronRight} />
          </IconButton>
        </Stack>
      </Box>

      <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography level="title-md">Magic Number:</Typography>
            <Input
              size="sm"
              type="number"
              value={editingMagicNumber && magicNumberDraft != null ? magicNumberDraft : magicNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!editingMagicNumber) return;
                const next = Number(e.target.value);
                setMagicNumberDraft(isNaN(next) ? 0 : round2(Math.max(0, next)));
              }}
              startDecorator={<IconWrapper icon={FaPoundSign} />}
              sx={{ width: 160 }}
              disabled={!editingMagicNumber}
            />
            <Button
              size="sm"
              variant="outlined"
              onClick={() => {
                if (!editingMagicNumber) {
                  // Enter edit mode
                  setMagicNumberDraft(magicNumber);
                  setEditingMagicNumber(true);
                  return;
                }
                // Save mode (session-only override; resets to the 12-month average on refresh)
                if (magicNumberDraft == null || magicNumberDraft <= 0) return;
                setMagicNumber(round2(magicNumberDraft));
                setHasSessionMagicOverride(true);
                setEditingMagicNumber(false);
                setMagicNumberDraft(null);
              }}
            >
              {editingMagicNumber ? 'Save' : 'Edit'}
            </Button>
          </Stack>
          <Typography level="body-sm" color="neutral">Revenue is summed from linked invoices for projects scheduled on each day (installation events).</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="sm" onClick={() => setShowManualModal(true)}>Add Manual Entry</Button>
            <Button size="sm" variant="outlined" onClick={() => setShowManageManual(true)}>Manage Manual Entries</Button>
            <Button size="sm" variant="soft" onClick={() => setShowOutgoings(true)}>Monthly Outgoings</Button>
          </Stack>
        </Stack>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size="lg" />
        </Box>
      ) : error ? (
        <Card color="danger" variant="soft" sx={{ p: 2, mb: 2 }}>
          <Typography>{error}</Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {viewMode === 'year' ? (
            visibleMonths.map(({ start, end }) => {
              // Aggregate month from daily aggregates
              let totalGross = 0;
              let totalNet = 0;
              let totalMagic = 0;
              let weekdaysCount = 0;
              for (let d = new Date(start); d < end; d = addDays(d, 1)) {
                const key = startOfDay(d).toISOString();
                const agg = (dayAggregates as any)[key];
                const dow = d.getDay();
                const isWeekdayDay = dow >= 1 && dow <= 5;
                if (isWeekdayDay) weekdaysCount += 1;
                if (agg) {
                  totalGross += agg.totalGross;
                  totalNet += agg.totalNet;
                  totalMagic += agg.totalMinusLabour;
                }
              }
              const monthTarget = weekdaysCount * magicNumber;
              const magicDelta = totalMagic - monthTarget;
              const monthLabel = `Magic Number (${formatCurrency(monthTarget)})`;
              const title = start.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
              return (
                <Grid key={start.toISOString()} xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography level="title-sm">{title}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip size="sm" color={magicDelta >= 0 ? 'success' : 'danger'} variant="soft">
                          {magicDelta >= 0 ? '+' : ''}{formatCurrency(magicDelta)} vs {monthLabel}
                        </Chip>
                      </Box>
                    </Stack>
                    <Stack spacing={1} sx={{ mb: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Revenue (incl. VAT)</Typography>
                        <Typography level="title-md">{formatCurrency(totalGross)}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Revenue (excl. VAT)</Typography>
                        <Typography level="title-md">{formatCurrency(totalNet)}</Typography>
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
              );
            })
            
          ) : (
          visibleDays.map((day) => {
            const key = startOfDay(day).toISOString();
            const agg = dayAggregates[key] || { totalGross: 0, totalNet: 0, totalMinusLabour: 0, totalAfterVat: 0, totalNetVatPayable: 0, totalLabour: 0, byCategoryGross: {}, byCategoryNet: {}, byCategoryAfterLabour: {}, byCategoryAfterVat: {}, distinctCategories: new Set<string>() };
            const dow = day.getDay();
            const isWeekend = dow === 0 || dow === 6;
            const dayTarget = isWeekend ? 0 : magicNumber;
            const companyPotBeforeLabour = (agg.totalMinusLabour || 0) + (agg.totalLabour || 0);
            const magicVariance = companyPotBeforeLabour - dayTarget;
            const varianceAbs = Math.abs(magicVariance);
            const magicLabel = `Magic Number (${formatCurrency(magicNumber)})`;
            const evsForDay = events.filter((e: { event_date: string; calendar_event_id?: string | null; end_date?: string | null; metadata?: any }) => {
              const current = startOfDay(new Date(key));
              if (!isWeekday(current)) return false;
              
              // Check if this day falls within any of the event's work ranges (main + additional)
              const baseDay = startOfDay(new Date(e.event_date));
              
              // Check main range
              let mainStart = baseDay;
              let mainEnd = baseDay;
              if (e.calendar_event_id && eventSpanById[e.calendar_event_id]) {
                const span = eventSpanById[e.calendar_event_id];
                mainStart = toDateOnly(span.start);
                mainEnd = toDateOnly(span.end);
              } else if (e.end_date) {
                mainStart = startOfDay(baseDay);
                mainEnd = toDateOnly(e.end_date);
              }
              
              // Check if current day is in main range
              if (current >= mainStart && current <= mainEnd) {
                return true;
              }
              
              // Check additional work ranges from metadata
              if (e.metadata && typeof e.metadata === 'object' && Array.isArray(e.metadata.additionalWorkRanges)) {
                for (const range of e.metadata.additionalWorkRanges) {
                  const rangeStart = toDateOnly(range.startDate);
                  const rangeEnd = toDateOnly(range.endDate || range.startDate);
                  if (current >= rangeStart && current <= rangeEnd) {
                    return true;
                  }
                }
              }
              
              return false;
            });

            return (
              <Grid key={key} xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography level="title-sm">{formatDate(day)}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="sm" color={magicVariance >= 0 ? 'success' : 'danger'} variant="soft">
                        {magicVariance >= 0 ? '+' : '-'}{formatCurrency(varianceAbs)} {magicVariance >= 0 ? 'over' : 'under'} Magic Number ({formatCurrency(magicNumber)})
                      </Chip>
                    </Box>
                  </Stack>

                  <Stack spacing={1} sx={{ mb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Revenue (incl. VAT)</Typography>
                      <Typography level="title-md">{formatCurrency(agg.totalGross)}</Typography>
                    </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Revenue (excl. VAT)</Typography>
                      <Typography level="title-md">{formatCurrency(agg.totalNet)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Company pot before labour</Typography>
                      <Typography level="title-md">{formatCurrency(companyPotBeforeLabour)}</Typography>
                  </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Variance vs Magic Number</Typography>
                      <Typography level="title-md" color={magicVariance >= 0 ? 'success' : 'danger'}>
                        {magicVariance >= 0 ? '+' : '-'}{formatCurrency(varianceAbs)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Profit (- all overheads)</Typography>
                      <Typography level="title-md" color={agg.totalMinusLabour < 0 ? 'danger' : undefined}>
                        {formatCurrency(agg.totalMinusLabour)}
                        </Typography>
                      </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Net VAT payable</Typography>
                      <Typography level="title-md">{formatCurrency(agg.totalNetVatPayable || 0)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm">Net profit after VAT</Typography>
                      <Typography level="title-md">{formatCurrency(agg.totalAfterVat)}</Typography>
                    </Stack>
                  </Stack>

                  {Object.entries(agg.byCategoryGross).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Stack spacing={0.5}>
                        {Object.entries(agg.byCategoryGross).map(([cat, grossAmount]) => {
                          const netAmount = agg.byCategoryNet[cat] || 0;
                          const afterLabourAmount = (agg.byCategoryAfterLabour && agg.byCategoryAfterLabour[cat]) || 0;
                          const afterVatAmount = (agg.byCategoryAfterVat && agg.byCategoryAfterVat[cat]) || 0;
                          const keyId = `${key}::${cat}`;
                          // Default to expanded; only collapse when explicitly toggled false
                          const isOpen = expandedByPerson[keyId] !== false;
                          return (
                            <Box key={cat}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography
                                  level="body-sm"
                                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                                  onClick={() =>
                                    setExpandedByPerson(prev => ({
                                      ...prev,
                                      [keyId]: !isOpen
                                    }))
                                  }
                                >
                                  {isOpen ? '▾' : '▸'} {cat}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography level="body-sm">{formatCurrency(grossAmount)} gross</Typography>
                                  <Typography level="body-sm" color="neutral">({formatCurrency(netAmount)} profit)</Typography>
                                </Stack>
                              </Stack>
                              {isOpen && (
                                <Stack spacing={0.25} sx={{ pl: 2, mt: 0.5 }}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                    <Typography level="body-xs" color="neutral">
                                      Profit (- all overheads): <span style={{ color: afterLabourAmount < 0 ? '#d32f2f' : 'inherit' }}>{formatCurrency(afterLabourAmount)}</span>
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                      Profit after VAT: {formatCurrency(afterVatAmount)}
                                    </Typography>
                                  </Stack>
                                  {evsForDay
                                    .filter((e: any) => {
                                      const catId = (e.calendar_event_id && eventSpanById[e.calendar_event_id] && eventCategoryById[e.calendar_event_id]) || (e as any).metadata?.category || 'unassigned';
                                      const catName = WORK_CATEGORIES.find(c => c.id === catId)?.name || catId;
                                      return catName === cat;
                                    })
                                    .map((e: any) => {
                                      // Ensure we have a project_id before rendering
                                      if (!e.project_id) return null;
                                      const invoiceId = projectInvoiceMap[e.project_id];
                                      const customerName = projectCustomerNameById[e.project_id];
                                      const projectTitle = projectNameById[e.project_id] || 'Project';
                                      const parts: string[] = [];
                                      if (customerName) parts.push(customerName);
                                      if (projectTitle) parts.push(projectTitle);
                                      const label = parts.join(' - ') || 'Project';
                                      
                                      if (invoiceId) {
                                        return (
                                          <Chip
                                            key={e.id || `${e.project_id}-${cat}`}
                                            size="sm"
                                            variant="outlined"
                                            component={RouterLink}
                                            to={`/docs/${invoiceId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                              cursor: 'pointer',
                                              alignSelf: 'flex-start',
                                              maxWidth: '100%',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {label}
                                          </Chip>
                                        );
                                      }
                                      return (
                                        <Chip
                                          key={e.id || `${e.project_id}-${cat}`}
                                          size="sm"
                                          variant="outlined"
                                          onClick={() => navigate(`/projects/${e.project_id}/workspace/implementation`)}
                                          sx={{
                                            cursor: 'pointer',
                                            alignSelf: 'flex-start',
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {label}
                                        </Chip>
                                      );
                                    })
                                    .filter(Boolean)}
                                  {/* Manual entries for this category/day as chips (show note if present) */}
                                  {(() => {
                                    const currentDate = startOfDay(new Date(key));
                                    const currentCatId = (WORK_CATEGORIES.find(c => c.name === cat)?.id) || cat;
                                    const matches = manualEntries.filter(m => {
                                      if (m.category_id !== currentCatId) return false;
                                      const s = toDateOnly(m.start_date);
                                      const e = toDateOnly(m.end_date || m.start_date);
                                      if (!isWeekday(currentDate)) return false;
                                      return currentDate >= s && currentDate <= e;
                                    });
                                    return matches.map(m => (
                                      <Chip
                                        key={`manual-${m.id}-${key}`}
                                        size="sm"
                                        variant="outlined"
                                        sx={{ alignSelf: 'flex-start' }}
                                      >
                                        {m.notes || 'Manual entry'}
                                      </Chip>
                                    ));
                                  })()}
                                </Stack>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Scheduled Projects removed; details available via By Person expanders */}
                </Card>
              </Grid>
            );
          })
          )}
          {viewMode === 'year' && yearSummary && (
            <Grid xs={12} sm={6} md={4}>
              <Card variant="solid" color="primary" sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography level="title-sm" sx={{ color: 'white' }}>Annual Summary</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="sm" color={yearSummary.magicDelta >= 0 ? 'success' : 'danger'} variant="solid">
                      {yearSummary.magicDelta >= 0 ? '+' : ''}{formatCurrency(yearSummary.magicDelta)} vs Magic Number
                    </Chip>
                  </Box>
                </Stack>
                <Stack spacing={1} sx={{ mb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="body-sm" sx={{ color: 'white' }}>Revenue (incl. VAT)</Typography>
                    <Typography level="title-md" sx={{ color: 'white' }}>{formatCurrency(yearSummary.totalGross)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="body-sm" sx={{ color: 'white' }}>Revenue (excl. VAT)</Typography>
                    <Typography level="title-md" sx={{ color: 'white' }}>{formatCurrency(yearSummary.totalProfit)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="body-sm" sx={{ color: 'white' }}>Profit (- all overheads)</Typography>
                    <Typography level="title-md" sx={{ color: yearSummary.totalMagic < 0 ? '#ff6b6b' : 'white' }}>
                      {formatCurrency(yearSummary.totalMagic)}
                    </Typography>
                  </Stack>
                  <Typography level="body-xs" sx={{ color: 'white' }}>Weekday Target ({yearSummary.weekdaysCount} days): {formatCurrency(yearSummary.target)}</Typography>
                </Stack>
                <Box sx={{ mt: 1.5 }}>
                  <Typography level="body-sm" sx={{ mb: 0.5, color: 'white' }}>Weekend Bonus (Sat–Sun)</Typography>
                  <Stack spacing={0.25}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-xs" sx={{ color: 'white' }}>Revenue (incl. VAT)</Typography>
                      <Typography level="body-xs" sx={{ color: 'white' }}>{formatCurrency(yearSummary.weekendBonusGross)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-xs" sx={{ color: 'white' }}>Revenue (excl. VAT)</Typography>
                      <Typography level="body-xs" sx={{ color: 'white' }}>{formatCurrency(yearSummary.weekendBonusProfit)}</Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          )}
          {weeklySummary && viewMode !== 'year' && (
            <Grid xs={12} sm={6} md={3}>
              <Card variant="solid" color="primary" sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography level="title-sm" sx={{ color: 'white' }}>
                    {viewMode === 'week' && 'Weekly Summary (Mon–Fri)'}
                    {viewMode === 'day' && 'Daily Summary'}
                    {viewMode === 'fortnight' && 'Fortnight Summary'}
                    {viewMode === 'month' && 'Monthly Summary'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="sm" color={weeklySummary.magicDelta >= 0 ? 'success' : 'danger'} variant="solid">
                      {weeklySummary.magicDelta >= 0 ? '+' : ''}{formatCurrency(weeklySummary.magicDelta)} vs Magic Number
                    </Chip>
                  </Box>
                </Stack>
                <Stack spacing={1} sx={{ mb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="body-sm" sx={{ color: 'white' }}>Revenue (incl. VAT)</Typography>
                    <Typography level="title-md" sx={{ color: 'white' }}>{formatCurrency(weeklySummary.totalGross)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="body-sm" sx={{ color: 'white' }}>Revenue (excl. VAT)</Typography>
                    <Typography level="title-md" sx={{ color: 'white' }}>{formatCurrency(weeklySummary.totalProfit)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="body-sm" sx={{ color: 'white' }}>Profit (- all overheads)</Typography>
                    <Typography level="title-md" sx={{ color: weeklySummary.totalMagic < 0 ? '#ff6b6b' : 'white' }}>
                      {formatCurrency(weeklySummary.totalMagic)}
                    </Typography>
                  </Stack>
                  <Typography level="body-xs" sx={{ color: 'white' }}>Weekday Target ({weeklySummary.weekdaysCount} days): {formatCurrency(weeklySummary.weeklyTarget)}</Typography>
                </Stack>
                <Box sx={{ mt: 1.5 }}>
                  <Typography level="body-sm" sx={{ mb: 0.5, color: 'white' }}>Weekend Bonus (Sat–Sun)</Typography>
                  <Stack spacing={0.25}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-xs" sx={{ color: 'white' }}>Revenue (incl. VAT)</Typography>
                      <Typography level="body-xs" sx={{ color: 'white' }}>{formatCurrency(weeklySummary.weekendBonusGross)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-xs" sx={{ color: 'white' }}>Revenue (excl. VAT)</Typography>
                      <Typography level="body-xs" sx={{ color: 'white' }}>{formatCurrency(weeklySummary.weekendBonusProfit)}</Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Monthly Outgoings card (always last) */}
          <Grid xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography level="title-sm">Outgoings ({outgoingsDisplay.label})</Typography>
                <Button size="sm" variant="outlined" onClick={() => setShowOutgoings(true)}>Manage</Button>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Select size="sm" value={outgoingsGranularity} onChange={(_, v) => setOutgoingsGranularity((v as any) || 'month')}>
                  <Option value="day">Daily</Option>
                  <Option value="week">Weekly</Option>
                  <Option value="month">Monthly</Option>
                  <Option value="quarter">Quarterly</Option>
                  <Option value="year">Annual</Option>
                </Select>
              </Stack>
              <Stack spacing={0.5} sx={{ mb: 1, maxHeight: 220, overflow: 'auto' }}>
                {outgoingsDisplay.list.length === 0 && (
                  <Typography level="body-sm" color="neutral">No outgoings in this period.</Typography>
                )}
                {outgoingsDisplay.list.map((o: any) => (
                  <Stack key={o.id} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack spacing={0}>
                      <Typography level="body-sm">{o.name}</Typography>
                      <Typography level="body-xs" color="neutral">
                        {(o.category || '').toString()} • {o.frequency || 'Monthly'}{o.vat_treatment === 'inclusive' ? ' • inc VAT' : o.vat_treatment === 'exclusive' ? ' • + VAT' : ''} {viewMode === 'year' ? `• ${new Date(o.start_date).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}` : ''}
                      </Typography>
                    </Stack>
                    <Typography level="body-sm">{formatCurrency(o.adjusted || 0)}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography level="body-sm">Total (as paid)</Typography>
                <Typography level="title-md">{formatCurrency(outgoingsDisplay.total)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography level="body-sm" color="neutral">Ex VAT</Typography>
                <Typography level="body-md">{formatCurrency(outgoingsDisplay.totalExVat)}</Typography>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Manual Entry Modal */}
      {showManualModal && (
        <Card
          variant="outlined"
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 2,
            zIndex: 1500,
            width: { xs: '95vw', sm: 600 },
            maxHeight: '85vh',
            overflow: 'auto'
          }}
        >
          <Typography level="title-sm" sx={{ mb: 1 }}>Add Manual Entry</Typography>
          <Stack spacing={1}>
            <Select placeholder="Category" value={manualCategory} onChange={(_, v) => setManualCategory((v as string) || '')}>
              {WORK_CATEGORIES.map(c => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
            <Input placeholder="Amount" type="number" value={manualAmount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualAmount(e.target.value)} />
            <Input placeholder="Start date" type="date" value={manualStart} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualStart(e.target.value)} />
            <Input placeholder="End date (optional)" type="date" value={manualEnd} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualEnd(e.target.value)} />
            
            {/* Additional work date ranges for non-consecutive jobs */}
            {manualAdditionalRanges.map((range, idx) => (
              <Stack key={idx} direction="row" spacing={2} alignItems="flex-end">
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Additional Date {idx + 1}</FormLabel>
                  <Input
                    type="date"
                    value={range.startDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const updated = [...manualAdditionalRanges];
                      updated[idx].startDate = e.target.value;
                      setManualAdditionalRanges(updated);
                    }}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    type="date"
                    value={range.endDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const updated = [...manualAdditionalRanges];
                      updated[idx].endDate = e.target.value;
                      setManualAdditionalRanges(updated);
                    }}
                  />
                </FormControl>
                <Button
                  size="sm"
                  variant="outlined"
                  color="danger"
                  onClick={() => {
                    setManualAdditionalRanges(manualAdditionalRanges.filter((_, i) => i !== idx));
                  }}
                >
                  Remove
                </Button>
              </Stack>
            ))}
            
            <Button
              size="sm"
              variant="outlined"
              onClick={() => {
                setManualAdditionalRanges([...manualAdditionalRanges, { startDate: manualStart, endDate: manualEnd || manualStart }]);
              }}
              startDecorator="+"
            >
              Add Work Date Range
            </Button>
            
            <Input placeholder="Notes *" value={manualNotes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualNotes(e.target.value)} required />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="sm" variant="outlined" onClick={() => setShowManualModal(false)}>Cancel</Button>
              <Button size="sm" onClick={async () => {
                if (!manualCategory || !manualAmount || !manualStart || !manualNotes || !manualNotes.trim()) {
                  alert('Please fill in all required fields, including Notes.');
                  return;
                }
                
                // Validate no overlapping dates
                const allRanges = [
                  { start: manualStart, end: manualEnd || manualStart },
                  ...manualAdditionalRanges.map(r => ({ start: r.startDate, end: r.endDate || r.startDate }))
                ];
                for (let i = 0; i < allRanges.length; i++) {
                  for (let j = i + 1; j < allRanges.length; j++) {
                    const r1 = allRanges[i];
                    const r2 = allRanges[j];
                    const r1Start = new Date(r1.start);
                    const r1End = new Date(r1.end);
                    const r2Start = new Date(r2.start);
                    const r2End = new Date(r2.end);
                    if (r1Start <= r2End && r2Start <= r1End) {
                      alert('Work date ranges cannot overlap. Please adjust the dates.');
                      return;
                    }
                  }
                }
                
                // Store additional ranges in notes as JSON (since we don't have metadata column)
                const notesData: any = { userNotes: manualNotes || null };
                if (manualAdditionalRanges.length > 0) {
                  notesData.additionalWorkRanges = manualAdditionalRanges;
                }
                const notesJson = Object.keys(notesData).length > 0 ? JSON.stringify(notesData) : (manualNotes || null);
                
                await directSupabase.from('manual_revenue_entries').insert({
                  category_id: manualCategory,
                  amount: Number(manualAmount),
                  start_date: manualStart,
                  end_date: manualEnd || manualStart,
                  notes: notesJson
                });
                setShowManualModal(false);
                setManualCategory(''); setManualAmount(''); setManualStart(''); setManualEnd(''); setManualNotes(''); setManualAdditionalRanges([]);
                // Trigger refresh by nudging date range (no-op)
                setManualRefreshKey(k => k + 1);
              }}>Save</Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {showManageManual && (
        <Card
          variant="outlined"
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 2,
            zIndex: 1550,
            width: { xs: '95vw', md: 700 },
            maxHeight: '85vh',
            overflow: 'auto'
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography level="title-sm">Manage Manual Entries</Typography>
            <Button size="sm" variant="outlined" onClick={() => setShowManageManual(false)}>Close</Button>
          </Stack>
          <Typography level="body-sm" sx={{ mb: 1 }}>Showing entries overlapping {dateRangeLabel(dateRange.start, dateRange.end)}</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="sm" stickyHeader>
            <thead>
              <tr>
                <th>Category</th>
                <th>Amount</th>
                <th>Start</th>
                <th>End</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {manualEntries.map((m) => {
                const cat = WORK_CATEGORIES.find(c => c.id === m.category_id)?.name || m.category_id;
                // Extract user notes from JSON if present
                let displayNotes = m.notes || '';
                if (m.notes) {
                  try {
                    const notesData = JSON.parse(m.notes);
                    if (notesData && typeof notesData === 'object' && notesData.userNotes) {
                      displayNotes = notesData.userNotes;
                    }
                  } catch {
                    // If notes isn't JSON, use as-is
                  }
                }
                return (
                  <tr key={m.id}>
                    <td>{cat}</td>
                    <td>£{(m.amount || 0).toFixed(2)}</td>
                    <td>{m.start_date}</td>
                    <td>{m.end_date || ''}</td>
                    <td>{displayNotes}</td>
                    <td>
                      <Stack direction="row" spacing={1}>
                        <Button size="sm" variant="outlined" onClick={() => {
                          // Parse notes to extract additionalRanges if present
                          let additionalRanges: Array<{ startDate: string; endDate: string }> = [];
                          let userNotes = m.notes || '';
                          if (m.notes) {
                            try {
                              const notesData = JSON.parse(m.notes);
                              if (notesData && typeof notesData === 'object') {
                                if (Array.isArray(notesData.additionalWorkRanges)) {
                                  additionalRanges = notesData.additionalWorkRanges;
                                }
                                if (notesData.userNotes) {
                                  userNotes = notesData.userNotes;
                                }
                              }
                            } catch {
                              // If notes isn't JSON, treat as plain text
                            }
                          }
                          setEditingEntry({ ...m, notes: userNotes, additionalRanges });
                        }}>Edit</Button>
                        <Button size="sm" color="danger" onClick={() => setDeleteTargetId(m.id)}>Delete</Button>
                      </Stack>
                    </td>
                  </tr>
                );
              })}
              {manualEntries.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <Typography level="body-sm">No manual entries in this range.</Typography>
                  </td>
                </tr>
              )}
            </tbody>
            </Table>
          </Box>
        </Card>
      )}

      {deleteTargetId && (
        <Card
          variant="outlined"
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 2,
            zIndex: 1700,
            width: { xs: '90vw', sm: 360 }
          }}
        >
          <Typography level="title-sm" sx={{ mb: 1 }}>Confirm Delete</Typography>
          <Typography level="body-sm" sx={{ mb: 2 }}>Are you sure you want to delete this manual entry?</Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="sm" variant="outlined" onClick={() => setDeleteTargetId(null)}>Cancel</Button>
            <Button size="sm" color="danger" onClick={async () => {
              const id = deleteTargetId;
              if (!id) return;
              await directSupabase.from('manual_revenue_entries').delete().eq('id', id);
              setDeleteTargetId(null);
              setManualRefreshKey(k => k + 1);
            }}>Delete</Button>
          </Stack>
        </Card>
      )}

      {editingEntry && (
        <Card
          variant="outlined"
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 2,
            zIndex: 1600,
            width: { xs: '95vw', sm: 600 },
            maxHeight: '85vh',
            overflow: 'auto'
          }}
        >
          <Typography level="title-sm" sx={{ mb: 1 }}>Edit Manual Entry</Typography>
          <Stack spacing={1}>
            <Select placeholder="Category" value={editingEntry.category_id} onChange={(_, v) => setEditingEntry(prev => prev ? { ...prev, category_id: (v as string) || '' } : prev)}>
              {WORK_CATEGORIES.map(c => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
            <Input placeholder="Amount" type="number" value={String(editingEntry.amount)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingEntry(prev => prev ? { ...prev, amount: Number(e.target.value || 0) } : prev)} />
            <Input placeholder="Start date" type="date" value={editingEntry.start_date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingEntry(prev => prev ? { ...prev, start_date: e.target.value } : prev)} />
            <Input placeholder="End date (optional)" type="date" value={editingEntry.end_date || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingEntry(prev => prev ? { ...prev, end_date: e.target.value || null } : prev)} />
            
            {/* Additional work date ranges for non-consecutive jobs */}
            {(editingEntry.additionalRanges || []).map((range, idx) => (
              <Stack key={idx} direction="row" spacing={2} alignItems="flex-end">
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Additional Date {idx + 1}</FormLabel>
                  <Input
                    type="date"
                    value={range.startDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const updated = [...(editingEntry.additionalRanges || [])];
                      updated[idx].startDate = e.target.value;
                      setEditingEntry(prev => prev ? { ...prev, additionalRanges: updated } : prev);
                    }}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    type="date"
                    value={range.endDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const updated = [...(editingEntry.additionalRanges || [])];
                      updated[idx].endDate = e.target.value;
                      setEditingEntry(prev => prev ? { ...prev, additionalRanges: updated } : prev);
                    }}
                  />
                </FormControl>
                <Button
                  size="sm"
                  variant="outlined"
                  color="danger"
                  onClick={() => {
                    setEditingEntry(prev => prev ? { ...prev, additionalRanges: (prev.additionalRanges || []).filter((_, i) => i !== idx) } : prev);
                  }}
                >
                  Remove
                </Button>
              </Stack>
            ))}
            
            <Button
              size="sm"
              variant="outlined"
              onClick={() => {
                setEditingEntry(prev => prev ? { ...prev, additionalRanges: [...(prev.additionalRanges || []), { startDate: prev.start_date, endDate: prev.end_date || prev.start_date }] } : prev);
              }}
              startDecorator="+"
            >
              Add Work Date Range
            </Button>
            
            <Input placeholder="Notes *" value={editingEntry.notes || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingEntry(prev => prev ? { ...prev, notes: e.target.value } : prev)} required />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="sm" variant="outlined" onClick={() => setEditingEntry(null)}>Cancel</Button>
              <Button size="sm" onClick={async () => {
                const e = editingEntry;
                if (!e) return;
                if (!e.notes || !e.notes.trim()) {
                  alert('Notes is required. Please enter a note.');
                  return;
                }
                
                // Validate no overlapping dates
                const allRanges = [
                  { start: e.start_date, end: e.end_date || e.start_date },
                  ...(e.additionalRanges || []).map(r => ({ start: r.startDate, end: r.endDate || r.startDate }))
                ];
                for (let i = 0; i < allRanges.length; i++) {
                  for (let j = i + 1; j < allRanges.length; j++) {
                    const r1 = allRanges[i];
                    const r2 = allRanges[j];
                    const r1Start = new Date(r1.start);
                    const r1End = new Date(r1.end);
                    const r2Start = new Date(r2.start);
                    const r2End = new Date(r2.end);
                    if (r1Start <= r2End && r2Start <= r1End) {
                      alert('Work date ranges cannot overlap. Please adjust the dates.');
                      return;
                    }
                  }
                }
                
                // Store additional ranges in notes as JSON
                const notesData: any = { userNotes: e.notes || null };
                if (e.additionalRanges && e.additionalRanges.length > 0) {
                  notesData.additionalWorkRanges = e.additionalRanges;
                }
                const notesJson = Object.keys(notesData).length > 0 ? JSON.stringify(notesData) : (e.notes || null);
                
                await directSupabase.from('manual_revenue_entries').update({
                  category_id: e.category_id,
                  amount: e.amount,
                  start_date: e.start_date,
                  end_date: e.end_date ?? e.start_date,
                  notes: notesJson
                }).eq('id', e.id);
                setEditingEntry(null);
                setManualRefreshKey(k => k + 1);
              }}>Save</Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {/* Monthly Outgoings Panel */}
      {showOutgoings && (
        <Card
          variant="outlined"
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 2,
            zIndex: 1800,
            width: { xs: '95vw', md: 820 },
            maxHeight: '85vh',
            overflow: 'auto'
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography level="title-sm">Monthly Outgoings</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="sm" onClick={() => setEditingOutgoing({ name: '', category: '', amount: '', start_date: new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1).toISOString().slice(0,10), end_date: new Date(anchorDate.getFullYear(), anchorDate.getMonth()+1, 0).toISOString().slice(0,10), noEndDate: false, frequency: 'Monthly', notes: '', vat_treatment: '' })}>Add</Button>
              <Button size="sm" variant="outlined" onClick={() => setShowOutgoings(false)}>Close</Button>
            </Stack>
          </Stack>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="sm" stickyHeader>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>VAT</th>
                  <th>Frequency</th>
                  <th>Month</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {outgoings.filter(o => o.name !== '__MAGIC_NUMBER__').map(o => (
                  <tr key={o.id}>
                    <td>{o.name}</td>
                    <td>{o.category || ''}</td>
                    <td>£{(o.amount || 0).toFixed(2)}</td>
                    <td>{VAT_TREATMENT_LABELS[o.vat_treatment || ''] || 'Not set'}</td>
                    <td>{o.frequency || 'Monthly'}</td>
                    <td>{o.start_date} → {o.end_date || 'No end date'}</td>
                    <td>{o.notes || ''}</td>
                    <td>
                      <Stack direction="row" spacing={1}>
                        <Button size="sm" variant="outlined" onClick={() => setEditingOutgoing({ id: o.id, name: o.name, category: o.category || '', amount: String(o.amount), start_date: o.start_date, end_date: o.end_date || '', noEndDate: !o.end_date, frequency: o.frequency, notes: o.notes || '', vat_treatment: o.vat_treatment || '' })}>Edit</Button>
                        <Button size="sm" color="danger" onClick={async () => {
                          await directSupabase.from('monthly_outgoings').delete().eq('id', o.id);
                          setOutgoingsRefreshKey(k => k + 1);
                        }}>Delete</Button>
                      </Stack>
                    </td>
                  </tr>
                ))}
                {outgoings.filter(o => o.name !== '__MAGIC_NUMBER__').length === 0 && (
                  <tr>
                    <td colSpan={8}><Typography level="body-sm">No outgoings in this period.</Typography></td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Typography level="title-sm">Total: {formatCurrency(outgoings.filter(o => o.name !== '__MAGIC_NUMBER__').reduce((s, o) => s + (o.amount || 0), 0))}</Typography>
          </Stack>
        </Card>
      )}

      {editingOutgoing && (
        <Card
          variant="outlined"
          sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', p: 2, zIndex: 1850, width: { xs: '95vw', sm: 420 }, maxHeight: '85vh', overflow: 'auto' }}
        >
          <Typography level="title-sm" sx={{ mb: 1 }}>{editingOutgoing.id ? 'Edit Outgoing' : 'Add Outgoing'}</Typography>
          <Stack spacing={1}>
            <Input placeholder="Name" value={editingOutgoing.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingOutgoing(prev => prev ? { ...prev, name: e.target.value } : prev)} />
            <Select placeholder="Category" value={editingOutgoing.category} onChange={(_, v) => setEditingOutgoing(prev => prev ? { ...prev, category: (v as string) || '' } : prev)}>
              {OUTGOING_CATEGORIES.map(c => (
                <Option key={c} value={c}>{c}</Option>
              ))}
            </Select>
            <Input placeholder="Amount" type="number" value={editingOutgoing.amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingOutgoing(prev => prev ? { ...prev, amount: e.target.value } : prev)} />
            <Select placeholder="VAT" value={editingOutgoing.vat_treatment || null} onChange={(_, v) => setEditingOutgoing(prev => prev ? { ...prev, vat_treatment: (v as VatTreatment) || '' } : prev)}>
              <Option value="inclusive">Amount includes VAT (20%)</Option>
              <Option value="exclusive">VAT added on top (20%)</Option>
              <Option value="none">No VAT (N/A)</Option>
            </Select>
            <Input placeholder="Start date" type="date" value={editingOutgoing.start_date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingOutgoing(prev => prev ? { ...prev, start_date: e.target.value } : prev)} />
            <Input placeholder="End date" type="date" disabled={editingOutgoing.noEndDate} value={editingOutgoing.noEndDate ? '' : editingOutgoing.end_date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingOutgoing(prev => prev ? { ...prev, end_date: e.target.value } : prev)} />
            <Checkbox
              size="sm"
              label="No end date"
              checked={editingOutgoing.noEndDate}
              onChange={(e) => setEditingOutgoing(prev => prev ? { ...prev, noEndDate: e.target.checked } : prev)}
            />
            <Select placeholder="Frequency" value={editingOutgoing.frequency} onChange={(_, v) => setEditingOutgoing(prev => prev ? { ...prev, frequency: (v as any) || 'Monthly' } : prev)}>
              <Option value="Once">Once</Option>
              <Option value="Daily">Daily (working days)</Option>
              <Option value="Monthly">Monthly</Option>
              <Option value="Quarterly">Quarterly</Option>
              <Option value="Annually">Annually</Option>
            </Select>
            <Input placeholder="Notes (optional)" value={editingOutgoing.notes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingOutgoing(prev => prev ? { ...prev, notes: e.target.value } : prev)} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="sm" variant="outlined" onClick={() => setEditingOutgoing(null)}>Cancel</Button>
              <Button size="sm" onClick={async () => {
                const sDate = editingOutgoing.start_date;
                const eDate = editingOutgoing.noEndDate ? null : (editingOutgoing.end_date || editingOutgoing.start_date);
                const firstOfMonth = (() => {
                  const d = new Date(sDate);
                  const fm = new Date(d.getFullYear(), d.getMonth(), 1);
                  return fm.toISOString().slice(0,10);
                })();
                const payload = {
                  name: editingOutgoing.name.trim(),
                  category: editingOutgoing.category.trim() || null,
                  amount: Number(editingOutgoing.amount || 0),
                  start_date: sDate,
                  end_date: eDate,
                  month: firstOfMonth,
                  frequency: editingOutgoing.frequency,
                  notes: editingOutgoing.notes.trim() || null,
                  vat_treatment: editingOutgoing.vat_treatment || null
                };
                const { error } = editingOutgoing.id
                  ? await directSupabase.from('monthly_outgoings').update(payload).eq('id', editingOutgoing.id)
                  : await directSupabase.from('monthly_outgoings').insert(payload);
                if (error) {
                  console.error('Save outgoing error:', error);
                  alert(/vat_treatment/i.test(error.message)
                    ? 'The VAT column is not in the database yet. Apply migration 20261017120000-outgoings-vat-inclusive.sql.'
                    : `Could not save the outgoing: ${error.message}`);
                  return;
                }
                setEditingOutgoing(null);
                setOutgoingsRefreshKey(k => k + 1);
              }}>Save</Button>
            </Stack>
          </Stack>
        </Card>
      )}
    </Box>
  );
};

export default RevenueCalendarPage;


